import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import type { InvokeChannelMap, InvokeChannel } from '@volley/crucible-ipc-types/ipc-channels';
import { INVOKE_PROJECT_METADATA, type ProjectRef } from '@volley/crucible-ipc-types';
import { useCrucibleClient } from '../transport/context';
import { projectQueryKey } from '../transport/project-query-key';

function invokeIpc<C extends InvokeChannel>(
  client: ReturnType<typeof useCrucibleClient>,
  channel: C,
  args: InvokeChannelMap[C][0],
): Promise<InvokeChannelMap[C][1]> {
  const invoke = client.invoke as (
    this: typeof client,
    channel: C,
    args?: InvokeChannelMap[C][0],
  ) => Promise<InvokeChannelMap[C][1]>;
  return args === undefined ? invoke.call(client, channel) : invoke.call(client, channel, args);
}

function projectRefFromArgs(args: unknown): ProjectRef | undefined {
  if (!args || typeof args !== 'object') return undefined;
  const record = args as Record<string, unknown>;
  const candidate =
    record.project && typeof record.project === 'object'
      ? (record.project as Record<string, unknown>)
      : record;
  if (
    (candidate.placement === 'local' || candidate.placement === 'cloud') &&
    typeof candidate.gameId === 'string'
  ) {
    return { placement: candidate.placement, gameId: candidate.gameId };
  }
  return undefined;
}

function ipcQueryKey<C extends InvokeChannel>(
  client: ReturnType<typeof useCrucibleClient>,
  channel: C,
  args: unknown,
): readonly unknown[] {
  const parts = args === undefined ? [channel] : [channel, args];
  const metadata = INVOKE_PROJECT_METADATA[channel];
  if (
    metadata.scope === 'global' ||
    metadata.scope === 'bind' ||
    metadata.scope === 'creation-session'
  ) {
    return parts;
  }
  const project = projectRefFromArgs(args);
  if (metadata.scope === 'workspace-or-creation-session' && !client.queryScope && !project) {
    return parts;
  }
  return projectQueryKey(client, parts, project);
}

/**
 * React Query wrapper for typed IPC invoke calls (read operations).
 *
 * For channels that take void args: `useIpcQuery('channel')`
 * For channels with args: `useIpcQuery('channel', args)`
 * Options (enabled etc.) can be passed as the last argument.
 */
export function useIpcQuery<C extends InvokeChannel>(
  channel: C,
  args: InvokeChannelMap[C][0],
  options?: { enabled?: boolean; staleTime?: number },
): UseQueryResult<InvokeChannelMap[C][1]> {
  const client = useCrucibleClient();

  return useQuery({
    queryKey: ipcQueryKey(client, channel, args),
    queryFn: () => invokeIpc(client, channel, args),
    enabled: options?.enabled,
    staleTime: options?.staleTime,
  });
}

/**
 * React Query mutation wrapper for typed IPC invoke calls (write operations).
 *
 * Optionally invalidates query keys after a successful mutation.
 */
export function useIpcMutation<C extends InvokeChannel>(
  channel: C,
  options?: { invalidateKeys?: string[][] },
): UseMutationResult<InvokeChannelMap[C][1], Error, InvokeChannelMap[C][0]> {
  const client = useCrucibleClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (args: InvokeChannelMap[C][0]) => invokeIpc(client, channel, args),
    onSuccess: () => {
      if (options?.invalidateKeys) {
        for (const key of options.invalidateKeys) {
          void queryClient.invalidateQueries({ queryKey: key });
        }
      }
    },
  });
}
